<template>
  <div v-if="isOpen" class="modal-overlay" @click="handleClose">
    <div class="modal-content" @click.stop>
      <h2 class="modal-title">Select Streaming Server</h2>
      <div class="line-divider"></div>

      <div class="modal-body">
        <p class="info-note">
          <vue-feather type="info" size="16" class="info-icon" />
          Make sure to select the server closest to your location for best
          streaming performance.
        </p>

        <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>

        <div v-if="isLoading" class="loading-state">
          <vue-feather type="loader" size="24" class="spin" />
          <span>Loading servers...</span>
        </div>

        <div v-else-if="servers.length === 0" class="empty-state">
          <vue-feather type="server" size="48" style="color: #9ca3af" />
          <p>No servers available at the moment.</p>
        </div>

        <div v-else class="servers-list">
          <div
            v-for="server in servers"
            :key="server.id"
            class="server-card"
            :class="{
              'server-card-selected': selectedServerId === server.id,
              'server-card-disabled': server.available === 0,
            }"
            @click="selectServer(server)"
          >
            <div class="server-card-header">
              <div class="server-info">
                <h3 class="server-name">{{ server.name }}</h3>
                <p v-if="server.region" class="server-region">
                  {{ server.region }}
                </p>
              </div>
              <div v-if="selectedServerId === server.id" class="selected-badge">
                <vue-feather type="check" size="16" />
              </div>
            </div>
            <div class="server-card-body">
              <div class="availability-info">
                <span class="availability-label">Available:</span>
                <span
                  class="availability-count"
                  :class="{
                    'availability-full': server.available === 0,
                    'availability-low':
                      server.available > 0 && server.available <= 2,
                    'availability-good': server.available > 2,
                  }"
                >
                  {{ server.available }} / {{ server.total }}
                </span>
              </div>
              <p v-if="server.available === 0" class="server-full-message">
                Server is full
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="button-group">
        <button
          class="modal-submit"
          @click="handleConfirm"
          :disabled="
            !selectedServerId ||
            isLoading ||
            isAssigning ||
            selectedServerId === currentServerId
          "
        >
          <div class="button-content">
            <div v-if="isAssigning" class="spinner"></div>
            <span>{{ isAssigning ? "Assigning..." : "Assign Server" }}</span>
          </div>
        </button>
        <button
          class="modal-cancel"
          @click="handleClose"
          :disabled="isAssigning"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { isMobile } from "@/utils/helper";
import {
  getAvailableServers,
  getServerAvailability,
  assignStreamToUser,
} from "@/services/streamingService";

export default {
  name: "ServerSelectionModal",
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    currentServerId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      servers: [],
      isLoading: false,
      isAssigning: false,
      selectedServerId: null,
      errorMessage: "",
    };
  },
  computed: {
    modalWidth() {
      return isMobile() ? "90%" : "600px";
    },
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        this.loadServers();
        this.selectedServerId = this.currentServerId;
      } else {
        this.errorMessage = "";
        this.selectedServerId = null;
      }
    },
    currentServerId(newVal) {
      this.selectedServerId = newVal;
    },
  },
  methods: {
    handleClose() {
      if (!this.isAssigning) {
        this.$emit("close");
      }
    },
    selectServer(server) {
      if (server.available === 0) {
        return; // Don't allow selection of full servers
      }
      this.selectedServerId = server.id;
      this.errorMessage = "";
    },
    async loadServers() {
      this.isLoading = true;
      this.errorMessage = "";
      try {
        const serverList = await getAvailableServers();
        // Get availability for each server
        const serversWithAvailability = await Promise.all(
          serverList.map(async (server) => {
            try {
              const availability = await getServerAvailability(server.id);
              return {
                ...server,
                available: availability.available,
                total: availability.total,
              };
            } catch (error) {
              console.error(
                `Error getting availability for server ${server.id}:`,
                error
              );
              return {
                ...server,
                available: 0,
                total: 0,
              };
            }
          })
        );
        this.servers = serversWithAvailability;
      } catch (error) {
        console.error("Error loading servers:", error);
        this.errorMessage = "Failed to load servers. Please try again.";
      } finally {
        this.isLoading = false;
      }
    },
    async handleConfirm() {
      if (!this.selectedServerId) {
        return;
      }

      this.isAssigning = true;
      this.errorMessage = "";

      try {
        const { auth } = await import("@/plugins/firebase");
        const userId = auth.currentUser?.uid;
        if (!userId) {
          throw new Error("User not authenticated");
        }

        // Auto-assign by not providing streamKey parameter
        await assignStreamToUser(userId, this.selectedServerId);
        this.$emit("server-assigned", this.selectedServerId);
        this.handleClose();
      } catch (error) {
        console.error("Error assigning server:", error);
        this.errorMessage =
          error.message || "Failed to assign server. Please try again.";
      } finally {
        this.isAssigning = false;
        this.handleClose();
      }
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.modal-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 1.5rem 1.5rem 0 1.5rem;
  color: #111827;
}

.line-divider {
  border: none;
  border-top: 1px solid #e5e7eb;
  margin: 1rem 1.5rem;
}

.modal-body {
  padding: 0 1.5rem;
  flex: 1;
  overflow-y: auto;
}

.info-note {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.75rem;
  background-color: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 6px;
  color: #92400e;
  font-size: 0.875rem;
  margin-bottom: 1.5rem;
  line-height: 1.5;
}

.info-icon {
  flex-shrink: 0;
  margin-top: 0.125rem;
  color: #d97706;
}

.error-message {
  color: #ef4444;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  padding: 0.75rem;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  gap: 1rem;
  color: #6b7280;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  gap: 1rem;
  color: #6b7280;
}

.empty-state p {
  font-size: 0.875rem;
}

.servers-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.server-card {
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  background: white;
}

.server-card:hover:not(.server-card-disabled) {
  border-color: #d1d5db;
  background-color: #f9fafb;
}

.server-card-selected {
  border-color: #000;
  background-color: #f9fafb;
}

.server-card-disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background-color: #f3f4f6;
}

.server-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
}

.server-info {
  flex: 1;
}

.server-name {
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 0.25rem 0;
}

.server-region {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
}

.selected-badge {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #000;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.server-card-body {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.availability-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.availability-label {
  font-size: 0.875rem;
  color: #6b7280;
}

.availability-count {
  font-size: 0.875rem;
  font-weight: 600;
}

.availability-full {
  color: #ef4444;
}

.availability-low {
  color: #f59e0b;
}

.availability-good {
  color: #10b981;
}

.server-full-message {
  font-size: 0.75rem;
  color: #ef4444;
  margin: 0;
  font-weight: 500;
}

.button-group {
  display: flex;
  gap: 0.75rem;
  padding: 1.5rem;
  border-top: 1px solid #e5e7eb;
}

.modal-submit {
  flex: 1;
  background-color: #000;
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: background-color 0.2s;
}

.modal-submit:hover:not(:disabled) {
  background-color: #1a1a1a;
}

.modal-submit:disabled {
  background-color: #9ca3af;
  cursor: not-allowed;
}

.modal-cancel {
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background-color: white;
  color: #374151;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s;
}

.modal-cancel:hover:not(:disabled) {
  background-color: #f9fafb;
  border-color: #9ca3af;
}

.modal-cancel:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.button-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 1s ease-in-out infinite;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 768px) {
  .button-group {
    flex-direction: column;
  }

  .modal-submit,
  .modal-cancel {
    width: 100%;
  }
}
</style>
